import { useEffect, useState } from 'react';
import { AlertTriangle, Copy, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, SectionCard } from '@/components/admin/page';
import { api, ApiError } from '../../lib/api';
import {
  displayEmail,
  displayIdentifier,
  displayPersonName,
  displayReferralCode,
  displayTelegramHandle,
  displayWorkspaceName,
  formatDate,
  formatDateTime,
  formatNumber,
  roleLabel as formatRoleLabel,
} from '../../lib/format';
import { Pager } from '../../lib/Pager';
import { useI18n } from '../../lib/i18n';

interface UserRow {
  id: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  locale: string | null;
  status: string;
  telegramUsername: string | null;
  telegramChatId: string | null;
  referralCode: string;
  referredByUserId: string | null;
  referrerEmail: string | null;
  referrerDisplayName: string | null;
  deletedAt: string | null;
}

interface DeletedUserRow {
  id: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean | null;
  createdAt: string | null;
  lastLoginAt: string | null;
  locale: string | null;
  status: string;
  telegramUsername: string | null;
  telegramChatId: string | null;
  referralCode: string | null;
  referredByUserId: string | null;
  referrerEmail: string | null;
  referrerDisplayName: string | null;
  deletedAt: string | null;
  source: 'soft' | 'hard';
  restorable: boolean;
  deletedBy: string | null;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

interface UserApiKey {
  keyId: string;
  deviceName: string | null;
  platform: string | null;
  status: string;
  lastSeenAt: string | null;
  deviceCreatedAt: string;
  keyPrefix: string;
  keyName: string;
  keyRole: string;
  keyCreatedAt: string;
  keyRevokedAt: string | null;
  keyLastUsedAt: string | null;
  tenantSlug: string;
  tenantName: string;
}

interface UserDetail {
  user: UserRow;
  memberships: { tenantId: string; role: string; tenantSlug: string; tenantName: string; joinedAt: string }[];
  apiKeys: UserApiKey[];
}

interface ListResponse {
  users: (UserRow | DeletedUserRow)[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 20;

const STATUS_KEY: Record<string, string> = {
  active: 'status.active',
  pending_review: 'status.pending_review',
  pending_telegram: 'status.pending_telegram',
  rejected: 'status.rejected',
  revoked: 'status.revoked',
  deleted: 'status.deleted',
};

export function UsersPage() {
  const { t } = useI18n();

  const statusLabel = (status: string): string => {
    const key = STATUS_KEY[status];
    return key ? t(key) : status;
  };

  const statusBadge = (status: string) => {
    const danger = status === 'rejected' || status === 'revoked' || status === 'deleted';
    const pending = status === 'pending_review' || status === 'pending_telegram';
    return <Badge variant={danger ? 'destructive' : pending ? 'warning' : 'success'}>{statusLabel(status)}</Badge>;
  };

  const roleLabel = (role: string): string => {
    return formatRoleLabel(t, role);
  };

  const [showDeleted, setShowDeleted] = useState(false);
  const [rows, setRows] = useState<(UserRow | DeletedUserRow)[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [committedQ, setCommittedQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', password: '', displayName: '', tenantId: '', role: '' });
  const [addLoading, setAddLoading] = useState(false);

  const buildParams = (overridePage?: number) => {
    const params = new URLSearchParams();
    if (committedQ) params.set('q', committedQ);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String((overridePage ?? page) * PAGE_SIZE));
    if (showDeleted) params.set('deleted', 'true');
    return params;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api<ListResponse>(`/v1/admin/users?${buildParams()}`);
        if (cancelled) return;
        setRows(r.users);
        setTotal(r.total);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, committedQ, showDeleted]);

  const search = () => { setPage(0); setCommittedQ(q.trim()); };

  const reloadPage = async () => {
    const r = await api<ListResponse>(`/v1/admin/users?${buildParams()}`);
    setRows(r.users);
    setTotal(r.total);
  };

  const openDetail = async (id: string) => {
    try {
      const d = await api<UserDetail>(`/v1/admin/users/${id}`);
      setDetail(d);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const deleteUser = async (id: string, email: string) => {
    if (!confirm(t('users.confirmDelete', { email: displayEmail(email, id) ?? email }))) return;
    try {
      await api(`/v1/admin/users/${id}`, { method: 'DELETE' });
      setDetail(null);
      await reloadPage();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const restoreUser = async (id: string, email: string) => {
    if (!confirm(t('users.confirmRestore', { email: displayEmail(email, id) ?? email }))) return;
    try {
      await api(`/v1/admin/users/${id}/restore-deleted`, { method: 'POST' });
      setDetail(null);
      await reloadPage();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const resetPassword = async (userId: string, email: string) => {
    const shownEmail = displayEmail(email, userId) ?? email;
    const newPw = prompt(t('access.prompt.resetPassword', { email: shownEmail }));
    if (newPw === null) return;
    if (newPw.length < 8) { alert(t('access.prompt.passwordTooShort')); return; }
    try {
      await api(`/v1/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: newPw }),
      });
      alert(t('access.prompt.passwordChanged', { email: shownEmail, pw: newPw }));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const revokeApiKey = async (userId: string, keyId: string, prefix: string) => {
    if (!confirm(`确认吊销密钥 ${prefix}？\n该密钥所连接的本地应用会立即断开，需重新配对。`)) return;
    try {
      await api(`/v1/admin/users/${userId}/api-keys/${keyId}/revoke`, { method: 'POST' });
      const d = await api<UserDetail>(`/v1/admin/users/${userId}`).catch(() => null);
      setDetail(d);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const userAction = async (
    id: string,
    action: 'revoke-access' | 'restore-access' | 'erase-server-data' | 'erase-server-data-and-request-device-wipe',
  ) => {
    const label =
      action === 'revoke-access'
        ? t('users.action.label.revoke')
        : action === 'restore-access'
          ? t('users.action.label.restore')
          : action === 'erase-server-data'
            ? t('users.action.label.erase')
            : t('users.action.label.eraseWipe');
    if (!confirm(t('users.action.confirm', { label }))) return;
    try {
      await api(`/v1/admin/users/${id}/${action}`, { method: 'POST' });
      const d = await api<UserDetail>(`/v1/admin/users/${id}`).catch(() => null);
      setDetail(d);
      await reloadPage();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const addUser = async () => {
    setAddLoading(true);
    try {
      await api('/v1/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: addForm.email.trim(),
          password: addForm.password,
          displayName: addForm.displayName.trim() || undefined,
          tenantId: addForm.tenantId.trim() || undefined,
          role: addForm.role.trim() || undefined,
        }),
      });
      alert(t('users.addUser.success', { email: addForm.email.trim() }));
      setAddForm({ email: '', password: '', displayName: '', tenantId: '', role: '' });
      setShowAddUser(false);
      await reloadPage();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('users.title')}
        description={t('users.desc')}
      />

      <SectionCard title={t('users.section')} description={t('users.sectionDesc', { n: formatNumber(total) })}>
        <div className="mb-3 flex gap-2">
          <Button
            variant={!showDeleted ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShowDeleted(false); setPage(0); }}
          >
            {t('users.tab.active')}
          </Button>
          <Button
            variant={showDeleted ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShowDeleted(true); setPage(0); }}
          >
            {t('users.tab.deleted')}
          </Button>
          <div className="ml-auto">
            <Button size="sm" onClick={() => setShowAddUser(true)}>{t('users.addUser')}</Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder={t('users.searchPh')}
            className="flex-1"
          />
          <Button onClick={search} disabled={loading}><Search />{loading ? t('common.searching') : t('common.search')}</Button>
        </div>
        {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="mt-5">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.email')}</TableHead>
                <TableHead>{t('common.name')}</TableHead>
                {!showDeleted && <TableHead>{t('users.col.referralCode')}</TableHead>}
                {!showDeleted && <TableHead>{t('users.col.referrer')}</TableHead>}
                <TableHead>{t('common.status')}</TableHead>
                {showDeleted && <TableHead>{t('users.deleted.type')}</TableHead>}
                {showDeleted && <TableHead>{t('users.col.deletedAt')}</TableHead>}
                {showDeleted && <TableHead>{t('users.deleted.deletedBy')}</TableHead>}
                {!showDeleted && <TableHead>{t('common.signedUp')}</TableHead>}
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => {
                const isDeletedView = showDeleted;
                const deletedRow = isDeletedView ? (u as DeletedUserRow) : null;
                const emailDisplay = u.email
                  ? displayEmail(u.email, u.id)
                  : <span className="text-muted-foreground text-xs italic">{t('users.deleted.emailErased')}</span>;
                return (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{emailDisplay}</TableCell>
                    <TableCell>{displayPersonName(u.displayName, u.email ?? undefined) ?? '-'}</TableCell>
                    {!isDeletedView && (
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs">{displayReferralCode((u as UserRow).referralCode)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            title={t('common.copy')}
                            onClick={async () => {
                              const ok = await copyToClipboard((u as UserRow).referralCode);
                              if (!ok) alert(t('common.errCopy'));
                            }}
                          >
                            <Copy className="size-3" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                    {!isDeletedView && (
                      <TableCell className="text-muted-foreground">
                        {u.referrerEmail
                          ? <span title={displayEmail(u.referrerEmail, u.referredByUserId) ?? undefined}>{displayPersonName(u.referrerDisplayName, u.referredByUserId ?? u.referrerEmail) ?? displayEmail(u.referrerEmail, u.referredByUserId)}</span>
                          : <span className="text-xs">{t('common.seed')}</span>}
                      </TableCell>
                    )}
                    <TableCell>{statusBadge(u.status)}</TableCell>
                    {isDeletedView && deletedRow && (
                      <TableCell>
                        <Badge variant={deletedRow.source === 'soft' ? 'success' : 'muted'}>
                          {t(deletedRow.source === 'soft' ? 'users.deleted.typeBadge.soft' : 'users.deleted.typeBadge.hard')}
                        </Badge>
                      </TableCell>
                    )}
                    {isDeletedView && (
                      <TableCell className="text-muted-foreground">{formatDate(u.deletedAt)}</TableCell>
                    )}
                    {isDeletedView && deletedRow && (
                      <TableCell className="text-muted-foreground text-xs">{deletedRow.deletedBy ?? '-'}</TableCell>
                    )}
                    {!isDeletedView && (
                      <TableCell className="text-muted-foreground">{formatDate(u.createdAt)}</TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isDeletedView && deletedRow ? (
                          deletedRow.restorable ? (
                            <Button variant="success" size="sm" onClick={() => restoreUser(u.id, u.email ?? '')}>{t('users.action.restoreDeleted')}</Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )
                        ) : (
                          <>
                            <Button variant="outline" size="sm" onClick={() => openDetail(u.id)}>{t('common.detail')}</Button>
                            <Button variant="destructive" size="sm" onClick={() => deleteUser(u.id, u.email ?? '')}>{t('users.action.softDelete')}</Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={showDeleted ? 9 : 7} className="py-10 text-center text-muted-foreground">{t('common.empty')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </SectionCard>

      {/* 添加用户对话框 */}
      <Dialog open={showAddUser} onOpenChange={(open) => { if (!open) setShowAddUser(false); }}>
        <DialogContent onClose={() => setShowAddUser(false)}>
          <DialogHeader>
            <DialogTitle>{t('users.addUser.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">{t('users.addUser.email')}</label>
              <Input
                value={addForm.email}
                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@example.com"
                type="email"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('users.addUser.password')}</label>
              <Input
                value={addForm.password}
                onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="至少 8 位"
                type="password"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('users.addUser.displayName')}</label>
              <Input
                value={addForm.displayName}
                onChange={(e) => setAddForm((f) => ({ ...f, displayName: e.target.value }))}
                placeholder={t('users.addUser.displayName')}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('users.addUser.tenantId')}</label>
              <Input
                value={addForm.tenantId}
                onChange={(e) => setAddForm((f) => ({ ...f, tenantId: e.target.value }))}
                placeholder="UUID"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('users.addUser.role')}</label>
              <Input
                value={addForm.role}
                onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                placeholder="member / admin / owner / viewer"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>{t('common.cancel')}</Button>
            <Button onClick={addUser} disabled={addLoading}>
              {addLoading ? t('common.creating') : t('users.addUser.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 用户详情对话框 */}
      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        {detail && (
          <DialogContent onClose={() => setDetail(null)}>
            <DialogHeader>
              <DialogTitle>{displayEmail(detail.user.email, detail.user.id)}</DialogTitle>
              <DialogDescription>
                {(displayPersonName(detail.user.displayName, detail.user.email) ?? t('users.detail.unnamed'))}
                {' · '}
                {displayIdentifier(detail.user.id, t('common.userId'))}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
              <div className="rounded-xl border bg-card p-4">
                <div className="mb-3 text-sm font-semibold">{t('users.detail.accountInfo')}</div>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('common.verified')}</dt><dd>{detail.user.emailVerified ? t('common.yes') : t('common.no')}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('common.status')}</dt><dd>{statusBadge(detail.user.status)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('common.telegramAccount')}</dt><dd>{displayTelegramHandle(detail.user.telegramUsername) ?? displayIdentifier(detail.user.telegramChatId, t('common.telegramChatId')) ?? '-'}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('common.locale')}</dt><dd>{detail.user.locale ?? '-'}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('common.signedUp')}</dt><dd>{formatDateTime(detail.user.createdAt)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('common.lastLogin')}</dt><dd>{formatDateTime(detail.user.lastLoginAt)}</dd></div>
                  {detail.user.deletedAt && (
                    <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{t('users.col.deletedAt')}</dt><dd>{formatDateTime(detail.user.deletedAt)}</dd></div>
                  )}
                </dl>
              </div>

              <div className="rounded-xl border bg-card p-4">
                <div className="mb-3 text-sm font-semibold">{t('users.detail.memberships', { n: detail.memberships.length })}</div>
                {detail.memberships.length === 0 ? (
                  <div className="text-sm text-muted-foreground">{t('users.detail.membershipsEmpty')}</div>
                ) : (
                  <div className="space-y-2">
                    {detail.memberships.map((m) => (
                      <div key={m.tenantId} className="rounded-lg border bg-muted/20 p-3 text-sm">
                        <div className="font-medium">
                          {displayWorkspaceName(m.tenantName)}
                          <span className="text-muted-foreground"> ({displayIdentifier(m.tenantSlug, t('common.slug'))})</span>
                        </div>
                        <div className="mt-1 flex gap-2 text-xs text-muted-foreground"><Badge variant="muted">{roleLabel(m.role)}</Badge><span>{t('common.joined')} {formatDate(m.joinedAt)}</span></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 rounded-xl border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold">配对的设备与接口密钥 ({detail.apiKeys.length})</div>
                <div className="text-xs text-muted-foreground">该用户名下已配对的本地应用</div>
              </div>
              {detail.apiKeys.length === 0 ? (
                <div className="text-sm text-muted-foreground">尚未配对任何设备。</div>
              ) : (
                <div className="space-y-2">
                  {detail.apiKeys.map((k) => {
                    const revoked = k.keyRevokedAt !== null || k.status === 'revoked';
                    return (
                      <div key={k.keyId} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-3 text-sm">
                        <div className="flex flex-1 flex-col">
                          <div className="font-medium">
                            {k.deviceName ?? k.keyName}
                            <span className="ml-2 text-xs text-muted-foreground">{displayIdentifier(k.keyPrefix, t('common.prefix'))}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="muted">{roleLabel(k.keyRole)}</Badge>
                            {k.platform && <span>{k.platform}</span>}
                            <span>{displayWorkspaceName(k.tenantName)}</span>
                            <span>· 配对 {formatDate(k.deviceCreatedAt)}</span>
                            {k.lastSeenAt && <span>· 最近活动 {formatDateTime(k.lastSeenAt)}</span>}
                            {revoked && <Badge variant="destructive">已吊销</Badge>}
                          </div>
                        </div>
                        {!revoked && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => revokeApiKey(detail.user.id, k.keyId, k.keyPrefix)}
                          >
                            解除配对
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Alert variant="warning" className="mt-5">
              <AlertTriangle className="mr-2 inline size-4" />
              <AlertDescription className="inline">
                {t('users.detail.wipeWarning')}
              </AlertDescription>
            </Alert>

            <DialogFooter className="justify-between">
              <div className="flex flex-wrap gap-2">
                <Button variant="warning" onClick={() => userAction(detail.user.id, 'revoke-access')}>{t('users.action.revoke')}</Button>
                <Button variant="success" onClick={() => userAction(detail.user.id, 'restore-access')}>{t('users.action.restore')}</Button>
                <Button variant="outline" onClick={() => resetPassword(detail.user.id, detail.user.email)}>{t('users.action.resetPassword')}</Button>
                <Button variant="outline" onClick={async () => {
                  if (!confirm('该用户所有在线设备的控制台锁定 PIN을 즉시 해제하시겠습니까?\n(오프라인 디바이스에는 적용되지 않음)')) return;
                  try {
                    const res = await api<{ sent: number; offline: number; message: string }>(
                      `/v1/admin/users/${detail.user.id}/reset-dashboard-pin`,
                      { method: 'POST' },
                    );
                    alert(`✓ ${res.message}`);
                  } catch (e) {
                    alert(e instanceof ApiError ? e.message : (e as Error).message);
                  }
                }}>解锁屏幕 PIN</Button>
                <Button variant="destructive" onClick={() => userAction(detail.user.id, 'erase-server-data-and-request-device-wipe')}>{t('users.action.eraseAndWipe')}</Button>
                {detail.user.deletedAt ? (
                  <Button variant="success" onClick={() => restoreUser(detail.user.id, detail.user.email)}>{t('users.action.restoreDeleted')}</Button>
                ) : (
                  <Button variant="destructive" onClick={() => deleteUser(detail.user.id, detail.user.email)}>{t('users.action.softDelete')}</Button>
                )}
              </div>
              <Button variant="outline" onClick={() => setDetail(null)}>{t('common.close')}</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
